import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import { TriangleAlert } from 'lucide-react'
import type { DisplayOccupant } from './types'

type Props = {
  occupantsByPlot: Record<string, DisplayOccupant[]>
  shardIndex: number
  neighborhoodType: 'guild' | 'charter'
}

const PlotConflicts = ({
  occupantsByPlot,
  shardIndex,
  neighborhoodType,
}: Props) => {
  const conflicts = Object.values(occupantsByPlot).filter((occupants) => {
    const claimed = occupants.find((occupant) => occupant.status === 'claimed')
    return (
      claimed &&
      occupants.some(
        (occupant) =>
          occupant.status === 'approved' &&
          occupant.shard_index === claimed.shard_index &&
          occupant.character.rowKey !== claimed.character.rowKey,
      )
    )
  })
  const totalClaimedPlots = Object.values(occupantsByPlot).filter((occupants) =>
    occupants.some((occupant) => occupant.status === 'claimed'),
  ).length

  if (conflicts.length > 0) {
    return (
      <div className='flex flex-col gap-3'>
        <h3 className='font-semibold mb-0'>Conflicts</h3>
        {conflicts.map((conflict) => {
          const claimed = conflict.find(
            (occupant) => occupant.status === 'claimed',
          )
          const approved = conflict.find(
            (occupant) => occupant.status === 'approved',
          )

          return (
            <Alert
              key={`${conflict[0].plot_id}-${conflict[0].shard_index}`}
              variant='warning'
            >
              <TriangleAlert className='h-4 w-4' />
              <AlertDescription>
                <span
                  className={
                    approved?.character.characterClass
                      ? classColours[approved.character.characterClass].text
                      : ''
                  }
                >
                  {approved?.character.name}
                </span>{' '}
                has an approved request for plot {conflict[0].plot_id}
                {conflict[0].shard_index !== shardIndex
                  ? ` (in subdivision ${conflict[0].shard_index + 1})`
                  : ''}
                , but{' '}
                <span
                  className={
                    claimed?.character.characterClass
                      ? classColours[claimed.character.characterClass].text
                      : ''
                  }
                >
                  {claimed?.character.name}
                </span>{' '}
                claimed it in-game.
              </AlertDescription>
            </Alert>
          )
        })}
      </div>
    )
  }

  if (totalClaimedPlots === 0 && neighborhoodType === 'guild') {
    return (
      <Alert>
        <AlertTitle>Synchronise with in-game state</AlertTitle>
        <AlertDescription>
          This page can be automatically synchronised with the in-game state of
          your neighborhood. Install the{' '}
          <a href='/desktop' target='_blank' rel='noreferrer'>
            desktop client
          </a>{' '}
          to get started.
        </AlertDescription>
      </Alert>
    )
  }

  return null
}

export default PlotConflicts
